/**
 * Remote tutoring pairs API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from '@/services/api-client';
import type {
  PairRecord,
  PairsResponse,
  PairQueryParams,
  UpdatePairManagerDto,
  DissociatePairDto,
  CreatePairOneOffSessionDto,
  PairHistoryEvent,
} from '@/types/remote/pair.types';
import type { Session } from '@/types/inperson/session.types';

/** `GET /companies/:id/binomes` — paginated remote pairs. */
export async function getPairs(
  companyId: string,
  params?: PairQueryParams,
): Promise<PairsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.search) searchParams.append('search', params.search);
  if (params?.statuses?.length) {
    searchParams.append('statuses', params.statuses.join(','));
  }
  if (params?.tutorUserId) searchParams.append('tutorUserId', params.tutorUserId);
  if (params?.studentUserId) searchParams.append('studentUserId', params.studentUserId);
  if (params?.establishmentId) searchParams.append('establishmentId', params.establishmentId);
  if (params?.schoolYearId) searchParams.append('schoolYearId', params.schoolYearId);
  if (params?.dateFrom) searchParams.append('dateFrom', params.dateFrom);
  if (params?.dateTo) searchParams.append('dateTo', params.dateTo);
  if (params?.coordinatorUserIds?.length) {
    params.coordinatorUserIds.forEach((id) => searchParams.append('coordinatorUserIds', id));
  }
  if (params?.managedByIds?.length) {
    params.managedByIds.forEach((id) => searchParams.append('managedByIds', id));
  }
  if (params?.programs?.length) {
    params.programs.forEach((p) => searchParams.append('programs', p));
  }

  const response = await apiClient.get<PairsResponse>(
    `/companies/${companyId}/binomes?${searchParams.toString()}`,
  );

  return response.data;
}

/** `GET /companies/:id/binomes/:pairId` */
export async function getPair(companyId: string, pairId: string): Promise<PairRecord> {
  const response = await apiClient.get<PairRecord>(`/companies/${companyId}/binomes/${pairId}`);
  return response.data;
}

/** `PATCH /companies/:id/binomes/:pairId/manager` */
export async function updatePairManager(
  companyId: string,
  pairId: string,
  dto: UpdatePairManagerDto,
): Promise<PairRecord> {
  const response = await apiClient.patch<PairRecord>(
    `/companies/${companyId}/binomes/${pairId}/manager`,
    dto,
  );
  return response.data;
}

/** `POST /companies/:id/binomes/:pairId/dissociate` */
export async function dissociatePair(
  companyId: string,
  pairId: string,
  dto: DissociatePairDto,
): Promise<PairRecord> {
  const response = await apiClient.post<PairRecord>(
    `/companies/${companyId}/binomes/${pairId}/dissociate`,
    dto,
  );
  return response.data;
}

/** `GET /companies/:id/binomes/:pairId/history` */
export async function getPairHistory(
  companyId: string,
  pairId: string,
): Promise<PairHistoryEvent[]> {
  const response = await apiClient.get<PairHistoryEvent[]>(
    `/companies/${companyId}/binomes/${pairId}/history`,
  );
  return response.data;
}

/**
 * `POST /companies/:companyId/binomes/:pairId/sessions` (ZDCD-1120).
 * `pairId` is the pair UUID (`PairRecord.id`), not the API `idBinome` (`bb-…`).
 * Body is only the slot; tutor / student / manager are resolved server-side from that pair.
 */
export async function createOneOffSession(
  companyId: string,
  pairId: string,
  dto: CreatePairOneOffSessionDto,
): Promise<Session> {
  const response = await apiClient.post<Session>(
    `/companies/${companyId}/binomes/${pairId}/sessions`,
    dto,
  );
  return response.data;
}

/** `GET /companies/:id/binomes/export` — Excel blob. */
export async function exportPairsToExcel(
  companyId: string,
  params?: PairQueryParams,
): Promise<Blob> {
  const searchParams = new URLSearchParams();

  if (params?.search) searchParams.append('search', params.search);
  if (params?.statuses?.length) {
    searchParams.append('statuses', params.statuses.join(','));
  }
  if (params?.tutorUserId) searchParams.append('tutorUserId', params.tutorUserId);
  if (params?.studentUserId) searchParams.append('studentUserId', params.studentUserId);
  if (params?.establishmentId) searchParams.append('establishmentId', params.establishmentId);
  if (params?.schoolYearId) searchParams.append('schoolYearId', params.schoolYearId);
  if (params?.dateFrom) searchParams.append('dateFrom', params.dateFrom);
  if (params?.dateTo) searchParams.append('dateTo', params.dateTo);
  if (params?.coordinatorUserIds?.length) {
    params.coordinatorUserIds.forEach((id) => searchParams.append('coordinatorUserIds', id));
  }
  if (params?.managedByIds?.length) {
    params.managedByIds.forEach((id) => searchParams.append('managedByIds', id));
  }
  if (params?.programs?.length) {
    params.programs.forEach((p) => searchParams.append('programs', p));
  }

  const response = await apiClient.get(
    `/companies/${companyId}/binomes/export?${searchParams.toString()}`,
    {
      responseType: 'blob',
      headers: {
        Accept: XLSX_MIME,
      },
    },
  );

  return response.data as Blob;
}
